import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

const withStateHandlersHOCGenerator = (Component) => {
  function WithStateHandlersHOC(props) {
    const { onChange, value: ownerValue } = props;
    const [dropdownDisplayed, setDropdownDisplayed] = useState(false);
    const [matches, setMatches] = useState([]);
    const [query, setQuery] = useState(null);
    const [innerValue, setInnerValue] = useState('');
    const [activeOptionIndex, setActiveOptionIndex] = useState(-1);

    useEffect(() => {
      setInnerValue(ownerValue);
    }, [ownerValue]);

    useEffect(() => {
      if (!dropdownDisplayed || matches.length === 0) {
        setActiveOptionIndex(-1);
      }
    }, [dropdownDisplayed, matches]);

    // Handlers
    const setValue = useCallback(
      (_value) => {
        setInnerValue(_value);
        setActiveOptionIndex(-1);
        onChange(_value);
      },
      [onChange]
    );
    const openDropdownWithMatches = useCallback((_matches) => {
      setMatches(_matches);
      setDropdownDisplayed(true);
    }, []);
    const closeDropdownAndResetMatches = useCallback(() => {
      setMatches([]);
      setDropdownDisplayed(false);
      setActiveOptionIndex(-1);
    }, []);
    const selectNextOption = useCallback(() => {
      if (!dropdownDisplayed || matches.length === 0) {
        return;
      }

      setActiveOptionIndex((currentIndex) => (currentIndex + 1 < matches.length ? currentIndex + 1 : 0));
    }, [dropdownDisplayed, matches]);
    const selectPreviousOption = useCallback(() => {
      if (!dropdownDisplayed || matches.length === 0) {
        return;
      }

      setActiveOptionIndex((currentIndex) => (currentIndex - 1 >= 0 ? currentIndex - 1 : matches.length - 1));
    }, [dropdownDisplayed, matches]);

    return (
      <Component
        {...props}
        activeOptionIndex={activeOptionIndex}
        dropdownDisplayed={dropdownDisplayed}
        matches={matches}
        query={query}
        value={innerValue}
        openDropdownWithMatches={openDropdownWithMatches}
        closeDropdownAndResetMatches={closeDropdownAndResetMatches}
        selectNextOption={selectNextOption}
        selectPreviousOption={selectPreviousOption}
        setActiveOptionIndex={setActiveOptionIndex}
        setQuery={setQuery}
        setValue={setValue}
      />
    );
  }

  WithStateHandlersHOC.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func,
  };

  return WithStateHandlersHOC;
};

const withStateHandlers = () => (Component) => withStateHandlersHOCGenerator(Component);

export default withStateHandlers;
